import axios from "axios";
import store from "@/store/index";
import router from "@/router";

// Base URLs
const backofficeBaseURL = process.env.VUE_APP_API_URL;

console.log("Backoffice URL::", backofficeBaseURL);

let cancelEndpoints = [];

// Provides get/post requests
const apiRequest = {
  /**
   * Finds and cancels previous requests matching the given endpoint.
   */
  findAndCancelPrevRequests: (deleteEndpoint) => {
    cancelEndpoints.forEach((cancelEndpoint) => {
      if (cancelEndpoint?.endpoint.includes(deleteEndpoint?.split("?")[0])) {
        cancelEndpoint.reqController.abort();
      }
    });

    // Remove canceled endpoints from the list
    cancelEndpoints = cancelEndpoints.filter(
      (cancelEndpoint) => !cancelEndpoint?.endpoint.includes(deleteEndpoint)
    );
  },

  /**
   * Cancels all ongoing requests.
   */
  cancelAllRequests: () => {
    cancelEndpoints.forEach((cancel) => cancel.reqController.abort());
    cancelEndpoints = [];
  },

  /**
   * Generalized API request function for both Backoffice & Public API requests.
   */
  sendRequest: async (method, endpoint, body, done, headerToken = null) => {
    if (!navigator.onLine) return;

    const customAxios = apiRequest.makeCustomAxiosInstance(
      headerToken,
      backofficeBaseURL
    );
    const { controller, id } = apiRequest.setAbortRequestData(endpoint);

    try {
      const response = await customAxios({
        method,
        url: endpoint,
        data: body,
        signal: controller.signal,
      });
      done(response);
    } catch (err) {
      // console.log("🔴 API Error caught in apiRequest.js:", {
      //   status: err?.response?.status,
      //   message: err?.response?.data?.message,
      //   error: err?.response?.data?.error,
      //   fullError: err,
      // });

      // Handle all HTTP errors consistently
      if (err?.response?.status === 401) {
        // console.log("🔐 Handling 401 Unauthorized");

        // Exception for BarTender authentication endpoint - don't logout user
        if (endpoint.includes("bartender/authenticate")) {
          // console.log("🚫 BarTender auth endpoint - skipping logout");
          store.dispatch("handleSnackBar", err);
        } else {
          apiRequest.unauthorizedToken();
        }
      } else if (err?.response?.status >= 400) {
        // console.log(
        //   "🚨 Dispatching error to store for status:",
        //   err?.response?.status
        // );
        // For all 4xx and 5xx errors, dispatch to store for consistent error handling
        store.dispatch("handleSnackBar", err);
      }
      done(err);
    } finally {
      apiRequest.removeCancelObg(id);
    }
  },

  /**
   * Backoffice API methods
   */
  backofficeGet: (endpoint, done, headerToken = null) =>
    apiRequest.sendRequest("get", endpoint, null, done, headerToken),

  backofficePost: (endpoint, body, done, headerToken = null) =>
    apiRequest.sendRequest("post", endpoint, body, done, headerToken),

  backofficeDelete: (endpoint, done, headerToken = null) =>
    apiRequest.sendRequest("delete", endpoint, null, done, headerToken),

  backofficePut: (endpoint, body, done, headerToken = null) =>
    apiRequest.sendRequest("put", endpoint, body, done, headerToken),

  backofficePatch: (endpoint, body, done, headerToken = null) =>
    apiRequest.sendRequest("patch", endpoint, body, done, headerToken),

  /**
   * Creates an Axios instance with the correct authorization header.
   */
  makeCustomAxiosInstance: (headerToken, baseURL) => {
    let token = JSON.parse(localStorage.getItem("token")) || null;
    if (headerToken) token = headerToken;
    let selectedStoreId = "";
    if (
      store?.getters?.getSelectedStore ||
      Object.keys(store?.getters?.getSelectedStore).length
    ) {
      selectedStoreId = store?.getters?.getSelectedStore?.id;
    }

    return axios.create({
      baseURL,
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
        "X-STORE-ID": selectedStoreId ? selectedStoreId : undefined,
      },
    });
  },

  /**
   * Handles unauthorized token response by clearing storage and redirecting.
   */
  unauthorizedToken: () => {
    store.dispatch("clearVuexStore");
    router.push("/");
  },

  /**
   * Sets up an abort controller for request cancellation.
   */
  setAbortRequestData: (endpoint) => {
    const id = Math.random().toString(36).substr(2, 9);
    const controller = new AbortController();

    cancelEndpoints.push({
      cancelId: id,
      endpoint: endpoint?.split("?")[0],
      reqController: controller,
    });

    return { controller, id };
  },

  /**
   * Removes canceled request objects from the list.
   */
  removeCancelObg: (objId) => {
    cancelEndpoints = cancelEndpoints.filter((item) => item.cancelId !== objId);
  },
};

export default apiRequest;
